import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { supabase } from '../supabase';
import { useAuth } from './AuthContext';
import { normalizeAllowedSiteIds, sanitizeAllowedSiteIds } from '../utils/siteAccess';
import {
  getStoredSelectedSiteId,
  persistSelectedSiteId,
  resolvePreferredSiteId,
  isValidSiteId,
  isConsolidatedPreference,
} from '../utils/sitePreferences';

/**
 * SiteContext
 * ------------------------------------------------------------------
 * Estado global de la sucursal seleccionada para toda la aplicación.
 *
 * Responsabilidades:
 *  - Cargar la lista de sitios (`sitios` activos) de la organización.
 *  - Resolver la sucursal preferida del usuario (localStorage / fallback).
 *  - Notificar a TODA la app cuando el usuario cambia de sucursal,
 *    SIN tener que recargar la página (`window.location.reload`).
 *  - Mantener `localStorage` sincronizado para conservar la elección
 *    entre sesiones y entre pestañas.
 *
 * Política de selección (lo que el usuario espera ver al entrar):
 *  - 0 sitios  → vacío (no hay nada que elegir).
 *  - 1 sitio   → ese, siempre y automáticamente.
 *  - >1 sitios → la preferencia de la sesión anterior si sigue siendo válida;
 *                si eligió "Consolidado", se respeta "Consolidado". Recién si
 *                nunca eligió nada queda vacío. NO se cae a `sites[0]`: sería
 *                el mismo anti-patrón que sacamos de FlujoFondos e
 *                ImportadorMaestro (imputar movimientos al local equivocado).
 */

const SiteContext = createContext({
  sites: [],
  selectedSiteId: '',
  selectedSite: null,
  loading: true,
  setSelectedSiteId: () => {},
  refreshSites: async () => {},
  isMultiSite: false,
});

const SELECTED_SITE_EVENT = 'impulza:selected-site-changed';

// Reintentos ante una carga fallida o vacia justo despues del login. El token
// recien emitido puede tardar en propagarse a las policies de RLS, y en esa
// ventana la consulta a `sitios` vuelve con error o con 0 filas aunque la
// sucursal exista y este activa.
const MAX_REINTENTOS = 3;
const BACKOFF_MS = [400, 1200, 2500];

export const SiteProvider = ({ children }) => {
  const { profile, organization } = useAuth();
  const [sites, setSites] = useState([]);
  const [selectedSiteId, setSelectedSiteIdState] = useState('');
  const [loading, setLoading] = useState(true);
  const lastFetchedScopeRef = useRef(null);
  const intentosRef = useRef(0);
  const reintentoTimerRef = useRef(null);

  const normalizedAllowedSiteIds = useMemo(
    () => normalizeAllowedSiteIds(profile?.allowed_site_ids),
    [profile?.allowed_site_ids]
  );
  const siteAccessScopeKey = useMemo(() => {
    const orgId = String(profile?.organization_id || '').trim();
    // El default de la org entra en la clave porque en el camino de dos
    // queries (Gastify) la organizacion llega despues del profile: sin esto,
    // la primera carga se resolvia sin fallback y no se volvia a intentar.
    const defaultSiteId = String(organization?.default_site_id || '');
    return `${orgId}::${normalizedAllowedSiteIds.join('|')}::${defaultSiteId}`;
  }, [profile?.organization_id, normalizedAllowedSiteIds, organization?.default_site_id]);

  // `selectedSiteId` se lee dentro de fetchSites pero NO debe estar en sus
  // dependencias: recrear el callback en cada cambio de sucursal reejecutaba
  // efectos que dependen de su identidad.
  const selectedSiteIdRef = useRef(selectedSiteId);
  useEffect(() => { selectedSiteIdRef.current = selectedSiteId; }, [selectedSiteId]);

  // -------------------------------------------------------------------------
  // Carga de sitios
  // -------------------------------------------------------------------------
  const fetchSitesRef = useRef(null);

  // Programa un reintento con backoff. Devuelve true si quedaba algun intento.
  const programarReintento = useCallback(() => {
    if (intentosRef.current >= MAX_REINTENTOS) return false;
    const delay = BACKOFF_MS[intentosRef.current] ?? 2500;
    intentosRef.current += 1;
    clearTimeout(reintentoTimerRef.current);
    reintentoTimerRef.current = setTimeout(() => {
      fetchSitesRef.current?.();
    }, delay);
    return true;
  }, []);

  const fetchSites = useCallback(async () => {
    const orgId = profile?.organization_id;
    if (!orgId) {
      setSites([]);
      setSelectedSiteIdState('');
      lastFetchedScopeRef.current = null;
      setLoading(false);
      return;
    }

    setLoading(true);
    let cargaExitosa = false;
    try {
      const { data, error } = await supabase
        .from('sitios')
        .select('id, nombre, direccion, depositos, cost_center_id, mp_pos_id, is_active')
        .eq('organization_id', orgId)
        .eq('is_active', true)
        .order('nombre', { ascending: true });

      if (error) {
        console.error('[SiteContext] Error cargando sitios:', error);
        setSites([]);
        // Un fallo NO se puede dar por definitivo: si marcamos el scope como ya
        // consultado, el guard del efecto impide reintentar y el usuario queda
        // con "Sin sitio" y el desplegable vacio hasta volver a loguearse.
        if (programarReintento()) return;
        return;
      }

      const safeSites = Array.isArray(data) ? data : [];

      // 0 sitios puede ser real (org sin sucursales) o el efecto de que las
      // policies todavia no ven el token nuevo. Reintentamos unas pocas veces
      // antes de aceptar el vacio; si de verdad no hay sucursales, el ultimo
      // intento deja el mismo resultado y no se pierde nada.
      if (safeSites.length === 0 && programarReintento()) return;

      intentosRef.current = 0;

      // Usuarios con acceso restringido solo ven (y pueden elegir) sus sucursales.
      const hasRestrictedAccess = normalizedAllowedSiteIds.length > 0;
      const allowedVisibleSiteIds = sanitizeAllowedSiteIds(normalizedAllowedSiteIds, safeSites);
      const visibleSites = hasRestrictedAccess
        ? safeSites.filter((site) => allowedVisibleSiteIds.includes(String(site.id)))
        : safeSites;

      setSites(visibleSites);

      const preferida = resolvePreferredSiteId(visibleSites, {
        currentSiteId: selectedSiteIdRef.current,
        // `default_site_id` vive en `organizations`, no en `profiles`: leerlo del
        // profile dejaba el fallback siempre vacio y los usuarios multi-sucursal
        // sin preferencia guardada (equipo nuevo, cache borrada) caian en
        // "Sin sitio". Si el usuario tiene acceso restringido y la sucursal por
        // defecto no es suya, resolvePreferredSiteId la descarta sola.
        fallbackSiteId: organization?.default_site_id || '',
        organizationId: orgId,
        fallbackToFirst: false,
      });

      const finalId = preferida || '';

      if (finalId !== selectedSiteIdRef.current) {
        setSelectedSiteIdState(finalId);
      }

      // Persistimos SIEMPRE que haya una sucursal concreta, aunque no haya
      // cambiado respecto del estado en memoria: es lo que migra la clave
      // global legada (`selected_site_id`) a la clave por organizacion. Sin
      // esto, un usuario con dos organizaciones se pisaba la preferencia de
      // una con la de la otra y el selector aparecia vacio "a veces".
      if (finalId) persistSelectedSiteId(finalId, orgId);

      cargaExitosa = true;
    } finally {
      // Solo despues de una carga exitosa damos el scope por consultado. Es lo
      // que habilita el guard del efecto a saltear futuras cargas.
      if (cargaExitosa) lastFetchedScopeRef.current = siteAccessScopeKey;
      setLoading(false);
    }
  }, [
    profile?.organization_id,
    organization?.default_site_id,
    normalizedAllowedSiteIds,
    siteAccessScopeKey,
    programarReintento,
  ]);

  // El reintento necesita la ultima version del callback sin volver a crearlo.
  useEffect(() => { fetchSitesRef.current = fetchSites; }, [fetchSites]);
  useEffect(() => () => clearTimeout(reintentoTimerRef.current), []);

  // -------------------------------------------------------------------------
  // Re-cargar sitios cuando cambia la organización o el acceso por sucursal
  // -------------------------------------------------------------------------
  useEffect(() => {
    if (!profile?.organization_id) {
      setSites([]);
      setSelectedSiteIdState('');
      lastFetchedScopeRef.current = null;
      setLoading(false);
      return;
    }
    if (lastFetchedScopeRef.current === siteAccessScopeKey) return;
    intentosRef.current = 0;
    // Al cambiar de organizacion arrancamos de la preferencia guardada de la
    // org NUEVA, no de la sucursal que quedo en memoria de la anterior.
    const guardada = getStoredSelectedSiteId(profile.organization_id);
    setSelectedSiteIdState(isConsolidatedPreference(guardada) ? '' : (guardada || ''));
    fetchSites();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [siteAccessScopeKey]);

  // -------------------------------------------------------------------------
  // Sincronización entre pestañas: si el usuario cambia de sucursal en otra
  // pestaña, esta también se entera (sin reload).
  // -------------------------------------------------------------------------
  useEffect(() => {
    const orgId = profile?.organization_id || '';
    const normalizar = (valor) => (isConsolidatedPreference(valor) ? '' : (valor || ''));
    const onStorage = (event) => {
      if (!String(event?.key || '').startsWith('selected_site_id')) return;
      const next = normalizar(getStoredSelectedSiteId(orgId));
      setSelectedSiteIdState((prev) => (prev === next ? prev : next));
    };
    const onCustom = (event) => {
      const next = normalizar(event?.detail?.siteId ?? getStoredSelectedSiteId(orgId));
      setSelectedSiteIdState((prev) => (prev === next ? prev : next));
    };
    window.addEventListener('storage', onStorage);
    window.addEventListener(SELECTED_SITE_EVENT, onCustom);
    return () => {
      window.removeEventListener('storage', onStorage);
      window.removeEventListener(SELECTED_SITE_EVENT, onCustom);
    };
  }, [profile?.organization_id]);

  // -------------------------------------------------------------------------
  // Cambio explícito de sucursal
  // -------------------------------------------------------------------------
  const setSelectedSiteId = useCallback(
    (nextId) => {
      const safeNext = nextId || '';
      // Validamos contra el listado actual; si no es válido, ignoramos
      if (safeNext && !isValidSiteId(sites, safeNext)) {
        console.warn('[SiteContext] siteId inválido, se ignora:', safeNext);
        return;
      }
      setSelectedSiteIdState(safeNext);
      // Vacio = "Consolidado", y se guarda como eleccion explicita para que
      // sobreviva a la recarga en vez de volver a quedar "sin elegir".
      persistSelectedSiteId(safeNext, profile?.organization_id);
      // Notificamos a otros listeners en la misma pestaña (los `storage`
      // events nativos no disparan en la pestaña que los originó).
      try {
        window.dispatchEvent(
          new CustomEvent(SELECTED_SITE_EVENT, { detail: { siteId: safeNext } })
        );
      } catch {
        // ignore
      }
    },
    [sites, profile?.organization_id]
  );

  // -------------------------------------------------------------------------
  // Memo de salida
  // -------------------------------------------------------------------------
  const selectedSite = useMemo(
    () => sites.find((s) => String(s.id) === String(selectedSiteId)) || null,
    [sites, selectedSiteId]
  );

  const value = useMemo(
    () => ({
      sites,
      selectedSiteId,
      selectedSite,
      loading,
      setSelectedSiteId,
      refreshSites: fetchSites,
      isMultiSite: (sites?.length || 0) > 1,
    }),
    [sites, selectedSiteId, selectedSite, loading, setSelectedSiteId, fetchSites]
  );

  return <SiteContext.Provider value={value}>{children}</SiteContext.Provider>;
};

export const useSite = () => useContext(SiteContext);

export const SELECTED_SITE_CHANGE_EVENT = SELECTED_SITE_EVENT;
